import React, { useState, useEffect } from 'react';
import axios from "axios";
import AdminHeaderNew from '../NewAdminPanel/Common/AdminHeader';
import AdminSideBarNew from '../NewAdminPanel/Common/AdminSidebarMnt';
import AdminFooterNew from '../NewAdminPanel/Common/AdminFooter';

const KVKWiseData = () => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [rows, setRows] = useState([]);
    const [finYear, setFinYear] = useState('');
    const [atariCode, setAtariCode] = useState(1);

    // table utilities: sorting and pagination
    const [sortKey, setSortKey] = useState('');
    const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const baseUrl = `${process.env.NODE_ENV === 'development' ? process.env.REACT_APP_DEV_MODE : process.env.REACT_APP_PRO_MODE}`;

    const generateFinancialYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = 1; i < 6; i++) {
            const year = currentYear - i;
            years.push(`${year}-${(year + 1).toString().slice(-2)}`);
        }
        return years;
    };

    const financialYears = generateFinancialYears();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        setRows([]);
        setErrorMessage('');
        try {
            const requestUrl = `${baseUrl}/api/MT/CentralDashBoardAll`;
            const payload = {
                flag: "KVKWise",
                finYear: finYear,
                atariCode: atariCode
            };

            const res = await axios.post(requestUrl, payload, {
                headers: { 
                    'accept': '*/*',
                    'Content-Type': 'application/json'
                }
            });

            const content = res?.data?.dataContent ?? [];
            setRows(Array.isArray(content) ? content : []);
            
            if (!content || content.length === 0) {
                setErrorMessage('No data available.');
            }
        } catch (e) {
            console.error('Error fetching data:', e);
            setErrorMessage('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        fetchData();
    };

    const handleFinYearChange = (e) => {
        setFinYear(e.target.value);
        setCurrentPage(1);
    };

    const handleAtariCodeChange = (e) => {
        setAtariCode(Number(e.target.value));
        setCurrentPage(1);
    };

    useEffect(() => {
        if (finYear || atariCode) {
            fetchData();
        }
    }, [finYear, atariCode]);

    const renderRows = () => {
        if (!rows || rows.length === 0) return null;

        const columns = [
            { key: 'kvkName', label: 'KVK Name' },
            { key: 'district', label: 'District' },
            { key: 'state', label: 'State' },
            { key: 'totalSurveys', label: 'Total Surveys' },
            { key: 'completedSurveys', label: 'Completed Surveys' },
            { key: 'pendingSurveys', label: 'Pending Surveys' },
        ];

        const getValue = (row, key) => {
            return row[key] ?? '';
        };

        const handleSort = (key) => {
            if (sortKey === key) {
                setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
            } else {
                setSortKey(key);
                setSortDir('asc');
            }
            setCurrentPage(1);
        };

        const exportToCsv = () => {
            const exportData = rows.filter(row => row.kvkName !== 'Total');
            
            const header = columns.map(c => c.label).join(',');
            const allRows = exportData.map(r => columns.map(c => {
                const val = String(getValue(r, c.key)).replace(/"/g, '""');
                return `"${val}"`;
            }).join(','));
            
            const dataRows = rows.filter(row => row.kvkName !== 'Total');
            const totals = {
                totalSurveys: dataRows.reduce((sum, row) => sum + (Number(row.totalSurveys) || 0), 0),
                completedSurveys: dataRows.reduce((sum, row) => sum + (Number(row.completedSurveys) || 0), 0),
                pendingSurveys: dataRows.reduce((sum, row) => sum + (Number(row.pendingSurveys) || 0), 0),
            };
            const totalRow = `"Total","","","${totals.totalSurveys}","${totals.completedSurveys}","${totals.pendingSurveys}"`;
            
            const csv = [header, ...allRows, totalRow].join('\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'kvk_wise_data.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        };

        const sortedRows = (() => {
            const filteredData = rows.filter(row => row.kvkName !== 'Total');
            
            if (!sortKey) return filteredData;
            const data = [...filteredData];
            data.sort((a, b) => {
                const av = String(getValue(a, sortKey)).toLowerCase();
                const bv = String(getValue(b, sortKey)).toLowerCase();
                if (av < bv) return sortDir === 'asc' ? -1 : 1;
                if (av > bv) return sortDir === 'asc' ? 1 : -1;
                return 0;
            });
            return data;
        })();

        const totalItems = sortedRows.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        const safePage = Math.min(currentPage, totalPages);
        const startIndex = (safePage - 1) * pageSize;
        const pageRows = sortedRows.slice(startIndex, startIndex + pageSize);

        const goToPage = (p) => {
            const np = Math.min(Math.max(1, p), totalPages);
            setCurrentPage(np);
        };

        const dataRows = rows.filter(row => row.kvkName !== 'Total');
        
        const totals = {
            totalSurveys: dataRows.reduce((sum, row) => sum + (Number(row.totalSurveys) || 0), 0),
            completedSurveys: dataRows.reduce((sum, row) => sum + (Number(row.completedSurveys) || 0), 0),
            pendingSurveys: dataRows.reduce((sum, row) => sum + (Number(row.pendingSurveys) || 0), 0),
        };

        return (
            <div className="table-responsive">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <label className="me-2">Page size</label>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 90 }}
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span className="ms-3">Total: {totalItems}</span>
                    </div>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={exportToCsv}>Export CSV</button>
                </div>
                <table className="table table-bordered table-striped table-sm">
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th
                                    key={c.key}
                                    role="button"
                                    onClick={() => handleSort(c.key)}
                                    title="Click to sort"
                                >
                                    {c.label}
                                    {sortKey === c.key ? (
                                        <span className="ms-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((r, idx) => (
                            <tr key={idx}>
                                {columns.map((c) => (
                                    <td key={c.key}>
                                        {String(getValue(r, c.key))}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {safePage === totalPages && (
                            <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                                <td>Total</td>
                                <td></td>
                                <td></td>
                                <td>{totals.totalSurveys}</td>
                                <td>{totals.completedSurveys}</td>
                                <td>{totals.pendingSurveys}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        Page {safePage} of {totalPages}
                    </div>
                    <div className="btn-group btn-group-sm" role="group">
                        <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(1)} disabled={safePage === 1}>{'<<'}</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1}>{'<'}</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages}>{'>'}</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages}>{'>>'}</button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <AdminHeaderNew />
            <AdminSideBarNew />

            <main id="main" className="main">
                <div className="pagetitle page_title_box d-flex align-items-center justify-content-between">
                    <nav>
                        <h1>KVK Wise Data</h1>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">Reports</li>
                            <li className="breadcrumb-item active">KVK Wise Data</li>
                        </ol>
                    </nav>
                </div>

                <section className="section">
                    <div className="row mb-3">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">Filter Options</h5>
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <label className="form-label">Financial Year</label>
                                            <select
                                                className="form-select"
                                                value={finYear}
                                                onChange={handleFinYearChange}
                                            >
                                                <option value="">Select Financial Year</option>
                                                {financialYears.map((year) => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                      
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="alert alert-danger mt-3" role="alert">{errorMessage}</div>
                    )}

                    <div className="row mt-3">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title">KVK Wise Record</h5>
                                    {rows.length > 0 ? (
                                        renderRows()
                                    ) : (
                                        !loading && !errorMessage && (
                                            <div className="text-center py-4">
                                                <p>No data available</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {loading && <div className="overlay"><div className="overlay__inner"><div className="overlay__content"><span className="spinner"></span></div></div></div>}
            </main>

            <AdminFooterNew />
        </>
    );
};

export default KVKWiseData;
